import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertCircle, User, Phone as PhoneIcon, MapPin, Store } from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';

// Feb 2026 — Public Customer Connect landing page. Buyers open this via the
// seller's Customer Link (or by scanning the QR). We look up the seller by
// slug, collect basic details (name + phone + optional city) and POST them
// to /api/connect/{slug}/join which creates or reuses the customer under
// that seller's tenant. Available regardless of the seller's plan.
export default function CustomerConnect() {
  const { slug } = useParams();
  const nav = useNavigate();
  const base = process.env.REACT_APP_BACKEND_URL;
  const [seller, setSeller] = useState(null);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(null);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${base}/api/connect/${slug}`)
      .then(({ data }) => setSeller(data.seller))
      .catch((e) => setErr(e?.response?.data?.detail || 'Business not found'));
  }, [slug, base]);

  const phoneOk = /^\+?\d{7,15}$/.test(phone.trim());
  const canSubmit = seller && name.trim().length >= 1 && phoneOk && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${base}/api/connect/${slug}/join`, {
        name: name.trim(),
        phone: phone.trim(),
        city: city.trim() || null,
      });
      setOk(data);
      // Feb 2026 — After successful connect, take the buyer directly into
      // the seller's existing buyer-facing AI chatbox by opening the seller's
      // most-recently published collection (backend sorts by created_at DESC).
      // Falls back to the seller's storefront (/s/:sid) when the seller has
      // not published any collection yet. No new routes, no new schema, no
      // Sales Brain change — reuses existing /c/:short_code chat which is
      // already keyed on (seller_id, buyer_id).
      try {
        const sid = data?.seller?.id;
        if (sid) {
          const { data: cat } = await axios.get(`${base}/api/sellers/${sid}/catalog`);
          const first = (cat?.collections || [])[0];
          if (first && (first.short_code || first.id)) {
            nav(`/c/${first.short_code || first.id}`, { replace: true });
            return;
          }
          nav(`/s/${sid}`, { replace: true });
        }
      } catch (_e) {
        // Non-blocking — the "You're connected!" panel below still shows the
        // success state so the buyer isn't stuck.
      }
    } catch (er) {
      setErr(er?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setSaving(false); }
  };

  if (err && !seller) return (
    <div className="min-h-screen bg-[#f7f2ef] flex items-center justify-center p-4">
      <div className="max-w-sm w-full bg-white rounded-2xl shadow-sm p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
        <div className="mt-2 font-semibold text-fab-text">{err}</div>
      </div>
    </div>
  );
  if (!seller) return (
    <div className="min-h-screen bg-[#f7f2ef] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-fab-text-muted animate-spin" />
    </div>
  );

  const initials = (seller.business_name || 'FS').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-[#f7f2ef]" data-testid="customer-connect-page">
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="flex justify-center mb-4">
          <BrandLogo height={28} />
        </div>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-br from-fab-primary-lighter to-white p-5 sm:p-6 flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-fab-primary text-white flex items-center justify-center font-display font-bold text-[20px] shadow-inner ring-2 ring-white">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold">Connect with</div>
              <div className="font-display font-bold text-fab-text text-[19px] truncate" data-testid="connect-seller-name">
                {seller.business_name || 'Seller'}
              </div>
              {seller.city && (
                <div className="text-[12px] text-fab-text-secondary flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {seller.city}
                </div>
              )}
            </div>
          </div>

          {ok ? (
            <div className="p-5 sm:p-6 text-center" data-testid="connect-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div className="font-display font-bold text-fab-text text-[17px] mt-3">You&apos;re connected!</div>
              <div className="text-[13px] text-fab-text-secondary mt-1 leading-relaxed">
                {ok.welcome}
              </div>
              <div className="text-[12px] text-fab-text-muted mt-3">
                {seller.business_name} will share their latest collection updates with you.
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="p-5 sm:p-6 space-y-3" data-testid="connect-form">
              <div className="text-[13px] text-fab-text-secondary">
                Enter your details to receive the latest collection updates from {seller.business_name}.
              </div>
              <div>
                <label className="text-[12px] font-semibold text-fab-text flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-fab-text-muted" /> Your name</label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Full name" autoComplete="name"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13.5px] focus:outline-none focus:border-fab-primary"
                  data-testid="connect-name"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-fab-text flex items-center gap-1.5"><PhoneIcon className="w-3.5 h-3.5 text-fab-text-muted" /> Mobile number</label>
                <input
                  type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98765 43210" autoComplete="tel" inputMode="tel"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13.5px] focus:outline-none focus:border-fab-primary"
                  data-testid="connect-phone"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-fab-text flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-fab-text-muted" /> City <span className="text-fab-text-muted font-normal">(optional)</span></label>
                <input
                  type="text" value={city} onChange={(e) => setCity(e.target.value)}
                  placeholder="City" autoComplete="address-level2"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13.5px] focus:outline-none focus:border-fab-primary"
                  data-testid="connect-city"
                />
              </div>
              {err && (
                <div className="text-[12.5px] bg-red-50 border border-red-200 text-red-700 rounded-lg p-2" data-testid="connect-error">{err}</div>
              )}
              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full py-2.5 rounded-lg bg-fab-primary text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="connect-submit"
              >
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</> : <><Store className="w-4 h-4" /> Connect with {seller.business_name || 'Seller'}</>}
              </button>
              <div className="text-[10.5px] text-fab-text-muted text-center pt-1">
                By connecting you agree to receive latest collection updates from {seller.business_name}.
              </div>
            </form>
          )}
        </div>

        <div className="text-[10.5px] text-fab-text-muted text-center mt-4">
          Powered by <span className="font-semibold">FABTEXS AI</span>
        </div>
      </div>
    </div>
  );
}
